import { timeAgo } from '@/utils/datetime';
import { Cooperative, EntityType, Market } from './entity';
import { Order, OrderStatus } from './order';
import { LineItem } from './lineItem';

export type ChatMessage = {
  id: string;
  type: 'order' | 'text';
  senderType: 'system' | 'entity';
  plainText: string;
  content: string;
  metaData: { [key: string]: unknown };
  roomId: string;
  senderId: string;
  receiverId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  createdAtMs: string;
  actioned: boolean;
};

export type ChatRoom = {
  id: string;
  marketId: string;
  cooperativeId: string;
  createdAt: string;
  updatedAt: string;
  deletedAt: null;
  messages: ChatMessage[];
  market: Market;
  cooperative: Cooperative;
};

export type ChatListItem = {
  id: string;
  name: string;
  senderId: string;
  receiverId: string;
  createAtMs: string;
  profile?: string;
  message: string;
};

export type ChatOrder = {
  id: string;
  number: string;
  status: OrderStatus;
  lineItems: LineItem[];
};

export type ActivityHighlight = {
  highlight: string;
  styles: string[];
};

export type MessageAction = {
  type:
    | 'confirm'
    | 'cancel'
    | 'out_for_delivery'
    | 'mark_as_delivered'
    | 'waiting_for_confirmation'
    | 'order_received'
    | 'rate'
    | 'refund';
  text: string;
  isDisabled: boolean;
};

export type MessageHighlight = {
  highlights: ActivityHighlight[];
  text: string;
};

export type OrderActivityTemplate = {
  actions: MessageAction[];
  message: MessageHighlight;
};

export type MessageTemplateBase = {
  order: Order;
  lineItems: LineItem[];
  showActions: boolean;
  showLineItems: boolean;
};

export type MessageTemplate = MessageTemplateBase & {
  cooperative?: OrderActivityTemplate;
  market?: OrderActivityTemplate;
};

export type OrderTemplate = MessageTemplateBase & {
  lineItem: LineItem;
  hasMoreItems: boolean;
  activity: OrderActivityTemplate;
};

export const getChatListItems = (
  chatRooms: ChatRoom[] | undefined,
  entityType: EntityType,
): ChatListItem[] => {
  return (
    chatRooms?.map(chatRoom => ({
      id: chatRoom.id,
      profile:
        entityType === 'MARKET'
          ? chatRoom.market.user.configuration.profile
          : chatRoom.cooperative.user.configuration.profile,
      senderId:
        entityType === 'MARKET' ? chatRoom.market.id : chatRoom.cooperative.id,
      receiverId:
        entityType === 'MARKET' ? chatRoom.cooperative.id : chatRoom.market.id,
      name:
        entityType === 'MARKET'
          ? chatRoom.cooperative.name
          : chatRoom.market.name,
      createAtMs: timeAgo(chatRoom.messages?.[0]?.createdAtMs ?? 0),
      message: chatRoom.messages?.[0]?.plainText ?? 'Start Converation',
    })) ?? []
  );
};

export const getChatOrderTemplate = (
  chatMessage: ChatMessage,
  entityType: EntityType,
): OrderTemplate => {
  const emptyActivityTemplate = {
    actions: [],
    message: {
      highlights: [],
      text: '',
    },
  };
  const orderMetaData = chatMessage.metaData as MessageTemplate;
  let orderActivty = orderMetaData.cooperative ?? emptyActivityTemplate;

  if (entityType === 'MARKET') {
    orderActivty = orderMetaData.market ?? emptyActivityTemplate;
  }

  return {
    order: orderMetaData.order,
    lineItems: orderMetaData.lineItems,
    lineItem: orderMetaData.lineItems?.[0] ?? {},
    activity: orderActivty,
    hasMoreItems: orderMetaData?.lineItems?.length > 1,
    showActions: orderMetaData.showActions,
    showLineItems: orderMetaData.showLineItems,
  };
};
